import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, CheckCircle2, CheckSquare, Clock, Plus, Search,
} from "lucide-react";
import { apiFetch, getToken } from "@/api";
import { ExecutiveView } from "@/components/ExecutiveView";
import { ProviderIcon } from "@/components/ProviderIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CARD = "rounded-xl border border-slate-200 bg-white p-4 shadow-sm";
const SECTION_HEADING = "text-base font-bold tracking-tight text-[var(--hcd-blue-dark)] "
  + "border-l-4 border-[var(--hcd-blue)] pl-3 md:text-lg";

const Section = ({ id, title, aside, children, testid }) => (
  <section aria-labelledby={id} data-testid={testid} className="space-y-4">
    <div className="flex flex-wrap items-end justify-between gap-2 border-b border-slate-200
                    pb-2">
      <h2 id={id} className={SECTION_HEADING}>{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

export default function PortalHome() {
  const [home, setHome] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState(null);
  const [view, setView] = useState("mine");
  const [exec, setExec] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/portal/home").then(async (r) => {
      if (!r.ok) { setError("We could not load your home page. Please sign in again."); return; }
      setHome(await r.json());
    });
  }, [navigate]);

  useEffect(() => {
    if (view !== "executive" || exec) return;
    apiFetch("/portal/executive").then(async (r) => {
      setExec(r.ok ? await r.json()
        : { unavailable: "The executive view needs permission to read reports." });
    });
  }, [view, exec]);

  const ask = async (event) => {
    event.preventDefault();
    if (!query.trim()) { navigate("/catalog"); return; }
    const r = await apiFetch(`/portal/find?q=${encodeURIComponent(query.trim())}`);
    if (!r.ok) { navigate(`/catalog?q=${encodeURIComponent(query.trim())}`); return; }
    const result = await r.json();
    if (result.confident && result.go_to) { navigate(result.go_to); return; }
    setFound(result);
  };

  if (error) {
    return <p className="p-6 text-sm text-red-900" data-testid="portal-error">{error}</p>;
  }
  if (!home) {
    return <p className="p-6 text-sm text-slate-600" data-testid="portal-loading">Loading…</p>;
  }

  const work = home.my_work;
  const needs = home.needs_you_now;
  const canSeeExecutive = home.can_see_executive;
  const administers = home.person.admin_provider_scopes.length > 0;
  const employeeView = view === "employee";

  const workCards = [
    ["My Open Tickets", work.my_open_tickets, "open-tickets", false],
    ["My Requests", work.my_requests, "my-requests", false],
    ["Pending My Approval", work.pending_my_approval, "pending-approval",
     work.pending_my_approval.count > 0],
    ["Assigned to Me", work.assigned_to_me, "assigned", work.assigned_to_me.count > 0],
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-10 p-4 lg:p-8" data-testid="portal-home">
      {(canSeeExecutive || administers) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="portal-view-switcher"
             role="group" aria-label="Choose which home page to see">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-600">
            Viewing
          </span>
          {[["mine", administers ? "My teams" : "My work"],
            ["employee", "Plain employee view"],
            ...(canSeeExecutive ? [["executive", "Executive view"]] : [])].map(([key, label]) => (
            <Button key={key} size="sm" variant={view === key ? "default" : "outline"}
                    data-testid={`portal-view-${key}`} onClick={() => setView(key)}
                    aria-pressed={view === key}
                    className={view === key ? "bg-[var(--hcd-blue)]" : undefined}>
              {label}
            </Button>
          ))}
          {employeeView && administers && (
            <span className="text-xs text-slate-600" data-testid="portal-view-note">
              This is what somebody without a team of their own sees.
            </span>
          )}
        </div>
      )}

      {view === "executive" ? (
        exec ? (exec.unavailable
          ? <p className="text-sm text-slate-600" data-testid="executive-unavailable">
              {exec.unavailable}
            </p>
          : <ExecutiveView data={exec} />)
          : <p className="text-sm text-slate-600" data-testid="executive-loading">
              Reading the reports…
            </p>
      ) : (
      <>
        {/* ------------------------------------------------------- 1. what needs you now */}
        <Section id="needs-heading" testid="portal-needs-you-now"
                 title="What needs you now"
                 aside={<span className="text-xs text-slate-600" data-testid="portal-needs-method">
                   {needs.method}
                 </span>}>
          {needs.all_clear ? (
            <p className="flex items-center gap-2 rounded-xl border border-emerald-200
                          bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
               data-testid="portal-needs-clear">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              {needs.all_clear_note}
            </p>
          ) : (
            <ul className="space-y-3">
              {needs.items.map((item) => (
                <li key={item.kind}
                    data-testid={`portal-needs-${item.kind}`}
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-xl
                                border-l-4 px-4 py-3 text-sm ${item.severity === "error"
                                  ? "border-l-red-700 border border-red-200 bg-red-50 text-red-900"
                                  : "border-l-amber-600 border border-amber-200 bg-amber-50 "
                                    + "text-amber-900"}`}>
                  <span className="flex items-start gap-2">
                    {item.severity === "error"
                      ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                      : <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
                    <span>
                      <strong data-testid={`portal-needs-${item.kind}-headline`}>
                        {item.headline}
                      </strong>
                      <span className="block text-xs">{item.detail}</span>
                    </span>
                  </span>
                  <Link to={item.link} className="flex items-center gap-1 font-semibold underline"
                        data-testid={`portal-needs-${item.kind}-link`}>
                    {item.link_label}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* -------------------------------------------------------------- 2. get help */}
        <section className="rounded-2xl bg-[var(--hcd-blue)] p-6 text-white shadow-md lg:p-8"
                 data-testid="portal-hero" aria-labelledby="hero-heading">
          <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/90"
                 data-testid="portal-today">{home.today}</p>
              <h1 id="hero-heading"
                  className="mt-2 text-3xl font-semibold sm:text-4xl lg:text-5xl"
                  data-testid="portal-greeting">
                {home.greeting}, {home.person.first_name}. How can we help?
              </h1>
              <form onSubmit={ask} className="mt-5 flex max-w-xl gap-2" role="search">
                <label htmlFor="portal-ask" className="sr-only">Describe what you need</label>
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4
                                     text-slate-400" aria-hidden="true" />
                  <Input id="portal-ask" data-testid="portal-ask-input" value={query}
                         className="bg-white pl-9 text-slate-900"
                         placeholder="Describe what you need, e.g. reset my password"
                         onChange={(e) => setQuery(e.target.value)} />
                </div>
                <Button type="submit" data-testid="portal-ask-submit"
                        className="bg-[var(--hcd-blue-dark)] hover:bg-[var(--hcd-blue-dark)]">
                  Search
                </Button>
              </form>
              {found && (
                <div className="mt-4 rounded-xl bg-white p-4 text-slate-900"
                     data-testid="portal-find-results">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">
                      {found.rows.length > 0
                        ? `Best matches for “${found.q}”`
                        : `Nothing matched “${found.q}”`}
                    </p>
                    <Button size="sm" variant="ghost" data-testid="portal-find-clear"
                            onClick={() => setFound(null)}>Clear</Button>
                  </div>
                  <ul className="mt-2 space-y-2 text-sm">
                    {found.rows.map((row, index) => (
                      <li key={`${row.kind}-${row.key}`}
                          className="flex flex-wrap items-center justify-between gap-2 border-b
                                     border-slate-100 pb-2"
                          data-testid={`portal-find-row-${row.kind}-${row.key}`}>
                        <span>
                          <span className="font-medium">{row.name}</span>
                          {index === 0 && (
                            <span className="ml-2 rounded bg-[#e6f2dc] px-1.5 py-0.5 text-[10px]
                                  font-semibold uppercase text-[var(--hcd-green)]"
                                  data-testid="portal-find-best">best match</span>
                          )}
                          <span className="block text-xs text-slate-600">
                            {row.provider_name ? `${row.provider_name} · ` : ""}{row.why}
                          </span>
                        </span>
                        <Button asChild size="sm" variant="outline"
                                data-testid={`portal-find-go-${row.kind}-${row.key}`}>
                          <Link to={row.link}>{row.action}</Link>
                        </Button>
                      </li>
                    ))}
                    {found.rows.length === 0 && (
                      <li className="text-slate-600" data-testid="portal-find-empty">
                        {found.empty_reason}
                      </li>
                    )}
                  </ul>
                  <p className="mt-2 text-xs text-slate-600" data-testid="portal-find-method">
                    {found.method}
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-3">
              {home.primary_actions.map((action) => (
                <Button key={action.kind} asChild data-testid={`portal-action-${action.kind}`}
                        className={`w-full justify-start ${action.kind === "issue"
                          ? "bg-[var(--hcd-green)] text-white hover:bg-[var(--hcd-green-hover)]"
                          : action.kind === "request"
                            ? "bg-white text-[var(--hcd-blue)] hover:bg-slate-100"
                            : "bg-[var(--hcd-blue-dark)] text-white "
                              + "hover:bg-[var(--hcd-blue-dark)]"}`}>
                  <Link to={action.path}>
                    {action.kind === "issue" && <AlertTriangle className="mr-2 h-4 w-4" />}
                    {action.kind === "request" && <Plus className="mr-2 h-4 w-4" />}
                    {action.kind === "tickets" && <CheckSquare className="mr-2 h-4 w-4" />}
                    {action.label}
                  </Link>
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------- 3. your work */}
        <Section id="mywork-heading" testid="portal-my-work" title="Your work"
                 aside={<Link to="/me" className="text-sm font-medium
                        text-[var(--hcd-blue-link)] underline" data-testid="portal-profile-link">
                   Everything in your profile
                 </Link>}>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {workCards.map(([label, row, id, urgent]) => (
              <Link key={id} to={row.link}
                    className={`block rounded-xl border p-4 shadow-sm transition-colors ${urgent
                      ? "border-amber-300 bg-amber-50 hover:border-amber-500"
                      : "border-slate-200 bg-white hover:border-[var(--hcd-blue)]"}`}
                    data-testid={`portal-work-${id}`}>
                <p className={`text-sm ${urgent ? "font-medium text-amber-900"
                  : "text-slate-600"}`}>{label}</p>
                <p className={`text-3xl font-semibold ${urgent ? "text-amber-900"
                  : "text-[var(--hcd-blue)]"}`} data-testid={`portal-work-${id}-count`}>
                  {row.count}
                </p>
                <p className={`text-xs ${urgent ? "text-amber-900" : "text-slate-600"}`}>
                  {row.note}
                </p>
              </Link>
            ))}
          </div>
        </Section>

        {/* -------------------------------------------------------- 4. browse by provider */}
        <Section id="providers-heading" testid="portal-providers"
                 title="Browse by service provider"
                 aside={<Link to="/catalog" className="text-sm font-medium
                        text-[var(--hcd-blue-link)] underline" data-testid="portal-catalog-link">
                   Browse the full catalog
                 </Link>}>
          <div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {home.providers.map((provider) => (
                <Link key={provider.key} to={provider.link}
                      className="group flex flex-col justify-between rounded-xl border
                                 border-slate-200 bg-white p-5 shadow-sm transition-all
                                 hover:-translate-y-0.5 hover:border-[var(--hcd-blue)]
                                 hover:bg-[#f0f7fc] hover:shadow-md"
                      data-testid={`portal-provider-${provider.key}`}>
                  <span className="flex items-start gap-3">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg
                                     bg-[var(--hcd-blue)] text-white shadow-sm transition-colors
                                     group-hover:bg-[var(--hcd-blue-dark)]">
                      <ProviderIcon name={provider.icon} />
                    </span>
                    <span>
                      <span className="block font-semibold text-slate-900">{provider.name}</span>
                      <span className="mt-1 block text-sm text-slate-600">
                        {provider.description
                         || "No description recorded on the provider."}
                      </span>
                    </span>
                  </span>
                  <span className="mt-4 flex items-center justify-between">
                    <span className="rounded-full bg-[#f0f7fc] px-2.5 py-0.5 text-xs font-semibold
                                     text-[var(--hcd-blue)]">
                      {provider.services} service{provider.services === 1 ? "" : "s"}
                    </span>
                    <ArrowRight className="h-4 w-4 text-[var(--hcd-blue)] transition-transform
                                           group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>
              ))}
              {home.providers.length === 0 && (
                <p className="text-sm text-slate-600" data-testid="portal-providers-empty">
                  No providers are published yet.
                </p>
              )}
            </div>

            <div className="space-y-4">
              {!employeeView && home.provider_panels.map((panel) => (
                <section key={panel.provider_key} className={CARD}
                         data-testid={`portal-panel-${panel.provider_key}`}
                         aria-labelledby={`panel-${panel.provider_key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <h3 id={`panel-${panel.provider_key}`}
                        className="text-base font-semibold md:text-lg">
                      {panel.provider_name} Provider Workspace
                    </h3>
                    <span className="rounded bg-[#e6f2dc] px-2 py-0.5 text-[10px] font-semibold
                                     uppercase text-[var(--hcd-green)]">admin</span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-[#f0f7fc] p-2"
                         data-testid={`panel-${panel.provider_key}-unassigned`}>
                      <p className="text-2xl font-semibold text-[var(--hcd-blue)]">
                        {panel.unassigned}
                      </p>
                      <p className="text-xs text-slate-600">Unassigned</p>
                    </div>
                    <div className="rounded-lg bg-amber-50 p-2"
                         data-testid={`panel-${panel.provider_key}-at-risk`}>
                      <p className="text-2xl font-semibold text-amber-900">
                        {panel.sla_at_risk}
                      </p>
                      <p className="text-xs text-amber-900">SLA at risk</p>
                    </div>
                    <div className="rounded-lg bg-red-50 p-2"
                         data-testid={`panel-${panel.provider_key}-breached`}>
                      <p className="text-2xl font-semibold text-red-900">{panel.breached}</p>
                      <p className="text-xs text-red-900">Breached</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">Team queue</span>
                      <Link to={panel.queue_link}
                            className="text-[var(--hcd-blue-link)] underline"
                            data-testid={`panel-${panel.provider_key}-queue-link`}>
                        {panel.open_total} open
                      </Link>
                    </div>
                    <p className="mt-1 text-xs text-slate-600"
                       data-testid={`panel-${panel.provider_key}-mix`}>
                      {Object.entries(panel.status_mix).map(([k, v]) => `${k} ${v}`).join(" · ")
                       || "nothing open"}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {panel.sla_sample_note}. {panel.method}
                    </p>
                  </div>
                  <div className="mt-3 border-t border-slate-200 pt-3">
                    <p className="text-[10px] font-semibold uppercase tracking-widest
                                  text-slate-600">configure</p>
                    <ul className="mt-1 space-y-1 text-sm">
                      {panel.configure.map((row) => (
                        <li key={row.label}>
                          <Link to={row.path}
                                className="text-[var(--hcd-blue-link)] hover:underline"
                                data-testid={`panel-${panel.provider_key}-cfg-${row.label
                                  .toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            {row.label} →
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              ))}
              {(employeeView || home.provider_panels.length === 0) && (
                <section className={CARD} data-testid="portal-no-panel">
                  <h3 className="text-base font-semibold md:text-lg">Your work</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    You do not administer a provider, so there is no team workspace here.
                    Everything you have asked for, and anything waiting on you, is on this page
                    and in your profile.
                  </p>
                </section>
              )}
            </div>
          </div>
        </Section>

        {/* ------------------------------------------------------------- 5. reference */}
        <Section id="reference-heading" testid="portal-reference" title="Reference">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className={CARD} data-testid="portal-popular">
              <h3 className="text-base font-semibold md:text-lg">Popular Services</h3>
              <p className="mt-1 text-xs text-slate-600" data-testid="portal-popular-method">
                {home.popular_services.method}
              </p>
              <ul className="mt-3 divide-y divide-slate-200 text-sm">
                {home.popular_services.rows.map((row) => (
                  <li key={row.service_key} className="flex items-center justify-between py-2"
                      data-testid={`portal-popular-${row.service_key}`}>
                    <Link to={row.link}
                          className="text-[var(--hcd-blue-link)] hover:underline">
                      {row.name}
                    </Link>
                    <span className="text-xs text-slate-600">{row.requests}</span>
                  </li>
                ))}
                {home.popular_services.rows.length === 0 && (
                  <li className="py-2 text-slate-600" data-testid="portal-popular-empty">
                    {home.popular_services.empty_reason}
                  </li>
                )}
              </ul>
            </div>

            <div className={CARD} data-testid="portal-articles">
              <h3 className="text-base font-semibold md:text-lg">Top Knowledge Articles</h3>
              <p className="mt-1 text-xs text-slate-600" data-testid="portal-articles-method">
                {home.top_articles.method}
              </p>
              <ul className="mt-3 divide-y divide-slate-200 text-sm">
                {home.top_articles.rows.map((row) => (
                  <li key={row.id} className="py-2" data-testid={`portal-article-${row.id}`}>
                    <Link to={row.link}
                          className="text-[var(--hcd-blue-link)] hover:underline">
                      {row.title}
                    </Link>
                  </li>
                ))}
                {home.top_articles.rows.length === 0 && (
                  <li className="py-2 text-slate-600" data-testid="portal-articles-empty">
                    Nothing is published that you may read yet.
                  </li>
                )}
              </ul>
            </div>

            <div className={CARD} data-testid="portal-announcements">
              <h3 className="text-base font-semibold md:text-lg">Announcements</h3>
              <ul className="mt-3 space-y-3 text-sm">
                {home.announcements.rows.map((row) => (
                  <li key={row.key} className="border-l-2 border-[var(--hcd-blue)] pl-3"
                      data-testid={`portal-announcement-${row.key}`}>
                    <p className="font-medium text-slate-900">{row.headline}</p>
                    <p className="text-slate-600">{row.body}</p>
                    <p className="text-xs text-slate-600">
                      {row.provider_key ? `${row.provider_key} · ` : ""}
                      {row.starts_on || "no start date recorded"}
                    </p>
                  </li>
                ))}
                {home.announcements.rows.length === 0 && (
                  <li className="text-slate-600" data-testid="portal-announcements-empty">
                    {home.announcements.empty_reason}
                  </li>
                )}
              </ul>
              <p className="mt-3 text-xs text-slate-600"
                 data-testid="portal-announcements-method">
                {home.announcements.method}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2" data-testid="portal-quick-links">
            {home.quick_links.map((row) => (
              <Button key={row.label} asChild variant="outline"
                      data-testid={`portal-quick-${row.label.toLowerCase()
                        .replace(/[^a-z]+/g, "-")}`}>
                <Link to={row.path}>{row.label}</Link>
              </Button>
            ))}
          </div>
        </Section>

        <p className="text-xs text-slate-600" data-testid="portal-method">{home.method}</p>
      </>
      )}
    </div>
  );
}
